import React from "react";
import { Box, Typography } from "@mui/material";
import Layout from "../Util/Layout";

const KvMemNetPage = () => {
    return (
        <Layout>
            <Box p={3}>
                <Typography variant="h3" gutterBottom>
                    KVMN Question Answering Agent - Educational Project
                </Typography>

                <Typography variant="h4" color="textSecondary" paragraph>
                    My Role: Solo Developer
                </Typography>

                {/* Project Description */}
                <Typography variant="h5" gutterBottom>
                    Project Overview
                </Typography>
                <Typography paragraph>
                    For this project, I built a Key-Value Memory Network from scratch in PyTorch and used it to answer
                    natural language questions about politicians. The idea behind a memory network is that the model doesn't
                    memorize facts in its weights at all, it learns how to look them up in an external dictionary. That
                    means the facts can change without ever retraining the model. The whole thing runs on
                    an attention mechanism that is very close to the self-attention inside a transformer, which made this
                    a great way to gain a good understanding of attention without the rest of the transformer complexity sitting on
                    top of it. This was built for my Georgia Tech Master of Science in Computer Science coursework, so I
                    am going to keep some implementation specifics vague. If you'd like to talk through the details in
                    private, reach out via the "Contact Me" page.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    The Data
                </Typography>
                <Typography paragraph>
                    The dataset is a scrape of Wikipedia biography infoboxes (the tables on the right side of a
                    person's page). Each row of those tables becomes a key/value pair ("birth date" → "11 january 1755"),
                    which is exactly the shape a memory network wants. I processed it down to politicians only, stripped
                    out image rows, ran everything through unidecode, lowercased it, dropped punctuation, and turned each
                    string into a bag-of-words multihot vector over a ~96k word vocabulary.
                </Typography>
                <Typography paragraph>
                    The catch is that the dataset has no questions in it. I generated them, writing three question
                    templates for each of the 17 most common relations and filling in the person's name. That narrows the
                    model to a pretty tight domain, which was a deliberate tradeoff to keep training times reasonable, and
                    not let scope creep take over for the project. I split the data 85/15,
                    but per person rather than per question, so a name the model trained on never shows up at test time.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Under the Hood
                </Typography>
                <Typography paragraph>
                    At a high level, the network learns to embed a question and a set of candidate keys into the same
                    space, score how well the question matches each key, and use those scores to pull out a blend of the
                    associated values that is weighted toward whichever one the question was actually asking about. A
                    second learned embedding is what lets that blended result get matched back to a real answer. I stuck
                    to single-hop retrieval and batched the work one person at a time, so every question about someone
                    shares the same stack of keys and values. Since this is coursework that runs again in future
                    semesters, I'm leaving the architecture and training loop specifics out here, happy to walk through
                    them privately.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Training and Performance
                </Typography>
                <Typography paragraph>
                    I trained with Adam at a 0.01 learning rate for 10 epochs at an embedding size of 128. All three of
                    those came from trial and error. I initially set 100 epochs, sketched the loss curve by hand as it
                    ran, and found the elbow sat right around 10. Embedding size 128 was the best size that I found from a
                    trial end error process. Each question is scored against a
                    pool of 50 candidate values: the person's real values plus randomly sampled distractors, so the true
                    answer never dominates the pool. Mean training loss dropped from 0.60 to 0.068, and the final test
                    numbers were 0.147 loss and 97.64% accuracy.
                </Typography>
                <Typography paragraph>
                    That accuracy number is flattering and I want to be upfront about why. The model is picking the right
                    answer out of 50 candidates, not out of every value in the database, so the task is far easier than
                    true global retrieval.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Asking It Questions
                </Typography>
                <Typography paragraph>
                    To actually use the model, I match names in the database against the text of the question, pull that
                    person's key/value pairs, run everything through the network, and return the text of the highest
                    scoring value. Ask it "When was alexander hamilton born?" and it gives back "11 january 1755". Within
                    its domain it holds up well, Nationalities, party affiliations, offices held, and places of residence
                    all come back correct on people it has never seen.
                </Typography>
                <Typography paragraph>
                    Outside that domain it falls apart in ways that are worth naming. Misspelling a name breaks the
                    lookup entirely. Anything requiring multi-hop reasoning ("the founding date of the country that
                    Hamilton was born in") is unsupported by design, since I only implemented single-hop retrieval. Additionally,
                    phrasing that strays too far from my templates sometimes gets misrouted, although performance in this area was
                    better than expected. Most of these trace back
                    to the same root cause: the model is only ever as general as the preprocessing that feeds it.
                </Typography>

                <Typography variant="h5" gutterBottom>
                    Technologies Used
                </Typography>
                <ul>
                    <li>
                        <Typography>
                            <strong>PyTorch:</strong> The network, the batched attention math, the training loop, and the
                            evaluation loop were all written from scratch.
                        </Typography>
                    </li>
                    <li>
                        <Typography>
                            <strong>Python &amp; NumPy:</strong> Used for all of the dataset parsing, tokenizing, and
                            bag-of-words vector construction.
                        </Typography>
                    </li>
                    <li>
                        <Typography>
                            <strong>Google Colab:</strong> Trained on a GPU runtime, with checkpointing so a long
                            training run didn't have to be repeated.
                        </Typography>
                    </li>
                </ul>

                {/* What I Learned */}
                <Typography variant="h5" gutterBottom>
                    What I Learned
                </Typography>
                <Typography paragraph>
                    The biggest thing I took away is that attention is a lot less mysterious once you've built one by
                    hand. Watching a softmax over key similarities actually select the right row out of a memory made the
                    transformer internals I'd read about click more thoroughly than lectures or readings on the subject were able to.
                    The second lesson was
                    about honesty in evaluation. My accuracy number looked very high until I traced back exactly what it
                    was measuring, and the interesting question turned out to be why it was inflated rather than how high
                    it was. Finally, this project made it obvious how much of a model's real-world capability is decided
                    during preprocessing. Nearly every failure case I found is clearly linked to a decision I made about the
                    data before the model ever saw it.
                </Typography>
            </Box>
        </Layout>
    );
};

export default KvMemNetPage;
